import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CloudDownload as DownloadIcon,
  CloudUpload as UploadIcon,
  FolderOff as EmptyIcon,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  SaveAlt as ImportIcon,
  Storage as KrollIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import {
  listPhoxDocuments,
  listKrollDocuments,
  fetchKrollDocumentBlobUrl,
  fetchPhoxDocumentDownloadUrl,
  importKrollDocument,
  updatePhoxDocument,
  deletePhoxDocument,
  PHOX_DOCUMENT_TYPE_LABELS,
  PhoxDocumentType,
  PhoxPatientDocument,
  KrollPatientDocument,
} from '../../../services/patientDocumentsService';
import PatientDocumentUpload from '../../patient-documents/PatientDocumentUpload';
import { usePharmacyStore } from '../../../stores/pharmacyStore';

interface PatientDocumentsPanelProps {
  patientId: string;
}

type SourceFilter = 'all' | 'kroll' | 'phox';

/**
 * `kroll_import` is a PHOX row that came from Kroll. It is deliberately its own
 * source rather than plain `phox` — GP-1383: once a document was imported the
 * list showed it as a brand-new PHOX upload and a pharmacist could no longer
 * tell it originated in Kroll.
 */
type RowSource = 'kroll' | 'phox' | 'kroll_import';

interface UnifiedRow {
  key: string;
  source: RowSource;
  title: string;
  typeLabel: string;
  uploadedBy: string;
  date: string;
  /** False when `date` is the PHOX import time because the Kroll row is unavailable. */
  dateIsOriginal: boolean;
  sizeBytes: number | null;
  mimeType: string | null;
  kroll?: KrollPatientDocument;
  phox?: PhoxPatientDocument;
  /** Provenance line shown on the Source chip of an imported row. */
  provenance?: string;
}

function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Kroll's MQTT size ceiling only blocks rows we must read THROUGH Kroll. Once a
 * document has a PHOX copy it is served from GCS, so an imported row stays
 * previewable/downloadable no matter how big the Kroll blob was.
 */
function oversizedInKroll(row: UnifiedRow): boolean {
  return !row.phox && Boolean(row.kroll?.tooLargeForPreview);
}

/**
 * How long the "Loading document…" overlay may wait for the viewer to report a
 * paint before it steps aside anyway. Some documents never fire `load` — a
 * cross-origin PDF the browser hands to its download path, for one — and a
 * spinner that never ends is a worse blank sheet than the one GP-1383 removed.
 */
/**
 * GP-1370: read the reason out of an Axios error the way NestJS actually
 * sends it. A `class-validator` rejection puts `message` on the body as a
 * string ARRAY; rendering that array straight into JSX concatenates the
 * reasons with no separator (the GP-322 / GP-1357 hazard). Join it instead,
 * and never fabricate a reason — say plainly when the server did not give one.
 */
export function describeApiError(err: any): string {
  const body = err?.response?.data?.message;
  if (Array.isArray(body)) {
    const joined = body.filter(Boolean).join('; ').trim();
    if (joined) return joined;
  } else if (typeof body === 'string' && body.trim()) {
    return body.trim();
  }
  if (typeof err?.message === 'string' && err.message.trim()) {
    return err.message.trim();
  }
  return 'Unknown error — no reason returned by the server';
}

const PREVIEW_PAINT_GRACE_MS = 10_000;

const RECATEGORIZE_OPTIONS: PhoxDocumentType[] = [
  'billing',
  'delivery',
  'clinical',
  'general',
];

/**
 * GP-370: unified Documents view — live Kroll documents (view-only, importable)
 * alongside PHOX-uploaded documents (pinnable, re-categorizable).
 */
export const PatientDocumentsPanel: React.FC<PatientDocumentsPanelProps> = ({
  patientId,
}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const activePharmacy = usePharmacyStore((state) => state.activePharmacy);

  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [search, setSearch] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  // `url` is null while the document bytes are still in flight — the dialog
  // opens immediately so the click has visible feedback (GP-1383).
  const [preview, setPreview] = useState<{
    title: string;
    url: string | null;
    mimeType: string | null;
  } | null>(null);
  /** Set once the <img>/<iframe> actually painted — a blob URL is not a render. */
  const [previewRendered, setPreviewRendered] = useState(false);
  const [previewLoadingKey, setPreviewLoadingKey] = useState<string | null>(null);
  // GP-1370: the import failure stays on screen with its reason and a Retry.
  // A toast that vanishes after a 9-second wait told the pharmacist nothing.
  const [importError, setImportError] = useState<{
    row: KrollPatientDocument;
    reason: string;
  } | null>(null);
  /**
   * Generation counter for the in-flight preview fetch. A Kroll blob takes
   * 1.5–2.7s, which is long enough for the user to close the dialog or click a
   * different row first — and a late response must not reopen a dialog they
   * dismissed or paint over the document they actually asked for.
   */
  const previewRequestIdRef = useRef(0);
  const previewAbortRef = useRef<AbortController | null>(null);
  /** The Kroll row awaiting confirmation in the "Save to PHOX" dialog. */
  const [importCandidate, setImportCandidate] =
    useState<KrollPatientDocument | null>(null);
  const [recatAnchor, setRecatAnchor] = useState<{
    el: HTMLElement;
    doc: PhoxPatientDocument;
  } | null>(null);

  // WP-10: only a Fillware pharmacy ties documents to Rx numbers, so only
  // there is the column asked for. The pharmacy is the PATIENT's (the first
  // half of the composite id), not the one the user has active. Every other
  // pharmacy's read is unchanged.
  const pharmacies = usePharmacyStore((state) => state.pharmacies);
  const patientPharmacyId = patientId?.includes('_') ? patientId.split('_')[0] : activePharmacy?.id;
  const withRxNumbers = pharmacies.find((p) => p.id === patientPharmacyId)?.databaseType === 'fillware';
  const phoxQuery = useQuery({
    queryKey: withRxNumbers ? ['patient-documents', patientId, 'with-rx-numbers'] : ['patient-documents', patientId],
    queryFn: () => (withRxNumbers ? listPhoxDocuments(patientId, { withRxNumbers: true }) : listPhoxDocuments(patientId)),
    enabled: Boolean(patientId),
  });

  const krollQuery = useQuery({
    queryKey: ['kroll-patient-documents', patientId],
    queryFn: () => listKrollDocuments(patientId),
    enabled: Boolean(patientId),
    staleTime: 30_000,
    retry: 1,
  });

  const invalidateBoth = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['patient-documents', patientId] });
    queryClient.invalidateQueries({
      queryKey: ['kroll-patient-documents', patientId],
    });
  }, [queryClient, patientId]);

  const undoImportMutation = useMutation({
    mutationFn: (docId: string) => deletePhoxDocument(patientId, docId),
    onSuccess: () => {
      enqueueSnackbar('Import undone — the document is only in Kroll again', {
        variant: 'success',
      });
      invalidateBoth();
    },
    onError: (err: any) => {
      enqueueSnackbar(
        `Failed to undo the import: ${describeApiError(err)}`,
        { variant: 'error' },
      );
    },
  });

  const importMutation = useMutation({
    mutationFn: (row: KrollPatientDocument) =>
      importKrollDocument(patientId, row.krollId, row.suggestedCategory),
    // GP-1370: a fresh attempt clears the previous failure banner.
    onMutate: () => setImportError(null),
    onSuccess: ({ alreadyImported, document }, row) => {
      if (alreadyImported) {
        // Import is idempotent per Kroll document id server-side, so a repeat
        // returns the existing row instead of minting a second copy.
        enqueueSnackbar(`"${row.title}" was already imported`, {
          variant: 'info',
        });
      } else {
        // A copy of a patient record was just created: offer the way back.
        enqueueSnackbar(`Imported "${row.title}" from Kroll`, {
          variant: 'success',
          action: (key) => (
            <Button
              size="small"
              sx={{ color: 'inherit' }}
              onClick={() => {
                closeSnackbar(key);
                undoImportMutation.mutate(document.id);
              }}
            >
              Undo
            </Button>
          ),
        });
      }
      invalidateBoth();
    },
    onError: (err: any, row) => {
      // The server now names the STAGE that broke (Kroll read / document
      // storage / the PHOX record) and whether retrying helps. Show that
      // message as sent — the old banner piped through "Internal server
      // error", which told nobody anything and offered no way to try
      // again. The underlying third-party/DB text stays server-side, in
      // the log line and Sentry (.claude/rules/security.md).
      setImportError({ row, reason: describeApiError(err) });
    },
  });

  /** krollId of the row whose import is in flight (undefined when idle). */
  const importingKrollId = importMutation.isPending
    ? importMutation.variables?.krollId
    : undefined;

  const pinMutation = useMutation({
    mutationFn: (doc: PhoxPatientDocument) =>
      updatePhoxDocument(patientId, doc.id, { pinned: !doc.pinned }),
    onSuccess: (updated) => {
      enqueueSnackbar(updated.pinned ? 'Document pinned' : 'Document unpinned', {
        variant: 'success',
      });
      invalidateBoth();
    },
    onError: (err: any) => {
      enqueueSnackbar(`Failed to update pin: ${err.message}`, {
        variant: 'error',
      });
    },
  });

  const recategorizeMutation = useMutation({
    mutationFn: ({
      doc,
      documentType,
    }: {
      doc: PhoxPatientDocument;
      documentType: PhoxDocumentType;
    }) => updatePhoxDocument(patientId, doc.id, { documentType }),
    onSuccess: (updated) => {
      enqueueSnackbar(
        `Category changed to ${PHOX_DOCUMENT_TYPE_LABELS[updated.documentType]}`,
        { variant: 'success' },
      );
      invalidateBoth();
    },
    onError: (err: any) => {
      enqueueSnackbar(`Failed to change category: ${err.message}`, {
        variant: 'error',
      });
    },
  });

  // Revoke blob URLs when the preview closes (avoids leaking object URLs)
  useEffect(() => {
    return () => {
      if (preview?.url?.startsWith('blob:')) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  /**
   * Invalidate (and abort) whatever preview fetch is in flight. Bumping the
   * generation is what makes a late response a no-op; the abort just stops the
   * request from finishing work nobody is waiting for.
   */
  const cancelPreviewRequest = useCallback(() => {
    previewRequestIdRef.current += 1;
    previewAbortRef.current?.abort();
    previewAbortRef.current = null;
  }, []);

  const closePreview = useCallback(() => {
    cancelPreviewRequest();
    setPreview(null);
    setPreviewRendered(false);
    setPreviewLoadingKey(null);
  }, [cancelPreviewRequest]);

  // Don't leave a request running for a panel that is gone.
  useEffect(() => () => cancelPreviewRequest(), [cancelPreviewRequest]);

  // The viewer has the bytes but has not reported a paint: give it a bounded
  // grace period, then reveal it rather than covering it with a spinner forever.
  useEffect(() => {
    if (!preview?.url || previewRendered) return;
    const timer = setTimeout(
      () => setPreviewRendered(true),
      PREVIEW_PAINT_GRACE_MS,
    );
    return () => clearTimeout(timer);
  }, [preview?.url, previewRendered]);

  const rows = useMemo<UnifiedRow[]>(() => {
    const krollDocs = krollQuery.data?.rows ?? [];
    // GP-1383: the merge key is the Kroll document id — NEVER the title. Titles
    // repeat across a patient's reports, and matching on them is what let an
    // import silently take over the Kroll row's identity.
    const krollById = new Map(krollDocs.map((d) => [d.krollId, d]));

    const phoxRows: UnifiedRow[] = (phoxQuery.data ?? []).map((d) => {
      const origin =
        d.krollPatDocumentId !== undefined && d.krollPatDocumentId !== null
          ? krollById.get(d.krollPatDocumentId)
          : undefined;
      const fromKroll =
        d.source === 'kroll_import' ||
        (d.krollPatDocumentId !== undefined && d.krollPatDocumentId !== null);

      // An imported row keeps showing the ORIGINAL Kroll author and date —
      // that is the provenance a pharmacist trusts. When the Kroll row is not
      // in the list window (or Kroll is down) we do NOT substitute the
      // importer for the author: the cell reads "—" and the Source chip
      // tooltip still names who imported it and when.
      const importedInto = `Imported into PHOX by ${d.uploadedBy} on ${
        d.createdAt ? format(new Date(d.createdAt), 'MMM d, yyyy h:mm a') : '—'
      }`;

      return {
        key: `phox-${d.id}`,
        source: fromKroll ? 'kroll_import' : 'phox',
        title: d.title || d.fileName,
        typeLabel: PHOX_DOCUMENT_TYPE_LABELS[d.documentType] ?? d.documentType,
        uploadedBy: fromKroll ? (origin?.uploadedByInit ?? '—') : d.uploadedBy,
        date: fromKroll && origin ? origin.dateCreated : d.createdAt,
        dateIsOriginal: !fromKroll || Boolean(origin),
        sizeBytes: d.fileSize ?? null,
        mimeType: d.mimeType ?? null,
        phox: d,
        kroll: origin,
        provenance: fromKroll
          ? origin
            ? `Kroll document #${origin.krollId} · ${origin.krollDocumentTypeLabel} · ${importedInto}`
            : `${importedInto}. The original Kroll row is outside the loaded window — its date and author are not shown.`
          : undefined,
      };
    });

    // Kroll rows already imported into PHOX are hidden from the Kroll side —
    // the merged PHOX row above carries their identity (GP-741: no duplicates;
    // GP-1383: no lost provenance).
    const krollRows: UnifiedRow[] = krollDocs
      .filter((d) => !d.alreadyImported)
      .map((d) => ({
        key: `kroll-${d.krollId}`,
        source: 'kroll',
        title: d.title || `Kroll document ${d.krollId}`,
        typeLabel: d.krollDocumentTypeLabel,
        uploadedBy: d.uploadedByInit ?? '—',
        date: d.dateCreated,
        dateIsOriginal: true,
        sizeBytes: d.contentBytes,
        mimeType: d.mimeType,
        kroll: d,
      }));

    const all = [...phoxRows, ...krollRows];

    const filtered = all.filter((r) => {
      // An imported document matches BOTH source filters — it is a PHOX copy of
      // a Kroll original, and hiding it from "Kroll" is how it went missing.
      if (sourceFilter === 'kroll' && r.source === 'phox') return false;
      if (sourceFilter === 'phox' && r.source === 'kroll') return false;
      if (search) {
        const q = search.toLowerCase();
        // An imported row displays the ORIGINAL Kroll initials, so the importer
        // is also searchable — otherwise "find what I imported" stopped working
        // the moment provenance started being preserved.
        const fields = [
          r.title,
          r.typeLabel,
          r.uploadedBy,
          r.phox?.uploadedBy ?? '',
        ];
        if (!fields.some((f) => f.toLowerCase().includes(q))) return false;
      }
      return true;
    });

    return filtered.sort((a, b) => {
      const aPinned = a.phox?.pinned ? 1 : 0;
      const bPinned = b.phox?.pinned ? 1 : 0;
      if (aPinned !== bPinned) return bPinned - aPinned;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [phoxQuery.data, krollQuery.data?.rows, sourceFilter, search]);

  /**
   * Preview is READ-ONLY (GP-1383). It fetches the document bytes and shows
   * them — it never imports, never writes, and never changes the row.
   *
   * The dialog opens the moment the eye is clicked, showing a spinner until the
   * bytes arrive AND the viewer paints. Before, the fetch took 1.5–2.7s with no
   * feedback and then the PDF viewer sat blank for seconds more, which is what
   * had users clicking around the action icons while they waited.
   *
   * Because that wait is seconds long, every result is checked against the
   * request generation before it is applied: closing the dialog or opening
   * another row abandons this fetch instead of letting it surface late.
   */
  const handlePreview = useCallback(
    async (row: UnifiedRow) => {
      if (row.kroll?.tooLargeForPreview && !row.phox) {
        enqueueSnackbar(
          `"${row.title}" is too large to preview in PHOX — open it in Kroll`,
          { variant: 'warning' },
        );
        return;
      }

      // Supersede any preview still loading — including one from another row.
      cancelPreviewRequest();
      const requestId = previewRequestIdRef.current;
      const controller = new AbortController();
      previewAbortRef.current = controller;
      const isStale = () => previewRequestIdRef.current !== requestId;

      setPreviewLoadingKey(row.key);
      setPreviewRendered(false);
      setPreview({ title: row.title, url: null, mimeType: null });
      try {
        // An imported document has a PHOX copy: read it from PHOX (cheaper than
        // a round trip through Kroll over MQTT).
        if (row.phox) {
          const { url } = await fetchPhoxDocumentDownloadUrl(
            patientId,
            row.phox.id,
            { signal: controller.signal },
          );
          if (isStale()) return;
          setPreview({
            title: row.title,
            url,
            mimeType: row.mimeType ?? 'application/pdf',
          });
        } else if (row.kroll) {
          const { url, mimeType, convertError } = await fetchKrollDocumentBlobUrl(
            patientId,
            row.kroll.krollId,
            { signal: controller.signal },
          );
          if (isStale()) {
            // The dialog was closed (or another row opened) while these bytes
            // were in flight. Drop them — and revoke the object URL by hand,
            // since it never reaches state for the cleanup effect to free.
            URL.revokeObjectURL(url);
            return;
          }
          if (convertError) {
            // Scanned TIFF that would not convert to PNG: the preview pane
            // cannot render it, so name the reason instead of opening a blank
            // viewer. Download still works — the bytes are the real document.
            URL.revokeObjectURL(url);
            setPreview(null);
            enqueueSnackbar(
              `"${row.title}" is a scanned TIFF that PHOX could not convert for preview — download it or open it in Kroll`,
              { variant: 'warning' },
            );
            return;
          }
          setPreview({ title: row.title, url, mimeType });
        }
      } catch (err: any) {
        // A stale rejection is our own abort, not a failure the user needs to
        // see — and the dialog it belonged to is already gone.
        if (isStale()) return;
        setPreview(null);
        enqueueSnackbar(
          `Failed to load "${row.title}": ${err?.response?.data?.message || err.message}`,
          { variant: 'error' },
        );
      } finally {
        if (!isStale()) setPreviewLoadingKey(null);
      }
    },
    [patientId, enqueueSnackbar, cancelPreviewRequest],
  );

  const handleDownload = useCallback(
    async (row: UnifiedRow) => {
      try {
        if (row.source === 'kroll' && row.kroll) {
          const { url } = await fetchKrollDocumentBlobUrl(
            patientId,
            row.kroll.krollId,
          );
          const a = document.createElement('a');
          a.href = url;
          a.download = row.title || `kroll-document-${row.kroll.krollId}`;
          a.click();
          setTimeout(() => URL.revokeObjectURL(url), 10_000);
        } else if (row.phox) {
          const { url } = await fetchPhoxDocumentDownloadUrl(
            patientId,
            row.phox.id,
          );
          window.open(url, '_blank', 'noopener');
        }
      } catch (err: any) {
        enqueueSnackbar(
          `Failed to download "${row.title}": ${err?.response?.data?.message || err.message}`,
          { variant: 'error' },
        );
      }
    },
    [patientId, enqueueSnackbar],
  );

  const loading = phoxQuery.isLoading || krollQuery.isLoading;
  const isPreviewImage = Boolean(preview?.mimeType?.startsWith('image/'));
  const previewBusy = Boolean(preview) && (!preview?.url || !previewRendered);

  return (
    <Paper elevation={2} sx={{ p: 2, width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
          Documents
        </Typography>
        <TextField
          size="small"
          placeholder="Search title / type / uploader"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <TextField
          size="small"
          select
          label="Source"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">All sources</MenuItem>
          <MenuItem value="kroll">Kroll</MenuItem>
          <MenuItem value="phox">PHOX</MenuItem>
        </TextField>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => setUploadOpen(true)}
        >
          Add Document
        </Button>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      {krollQuery.isError && (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={
            <Button size="small" onClick={() => krollQuery.refetch()}>
              Retry
            </Button>
          }
        >
          Could not load Kroll documents:{' '}
          {(krollQuery.error as any)?.response?.data?.message ||
            (krollQuery.error as Error)?.message}
          . PHOX-uploaded documents are still shown.
        </Alert>
      )}
      {krollQuery.data?.truncated && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Showing the {krollQuery.data.rows.length} most recent of{' '}
          {krollQuery.data.totalCount} Kroll documents — older documents are
          still available in Kroll.
        </Alert>
      )}
      {importError && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          data-testid="document-import-error"
          action={
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                disabled={importMutation.isPending}
                onClick={() => importMutation.mutate(importError.row)}
              >
                Retry
              </Button>
              <Button
                size="small"
                color="inherit"
                onClick={() => setImportError(null)}
              >
                Dismiss
              </Button>
            </Stack>
          }
        >
          Could not import "{importError.row.title}": {importError.reason}
        </Alert>
      )}
      {phoxQuery.isError && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          action={
            <Button size="small" onClick={() => phoxQuery.refetch()}>
              Retry
            </Button>
          }
        >
          Could not load PHOX documents:{' '}
          {(phoxQuery.error as Error)?.message}
        </Alert>
      )}

      {!loading && rows.length === 0 && !phoxQuery.isError && !krollQuery.isError && (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <EmptyIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography>
            {search || sourceFilter !== 'all'
              ? 'No documents match the current filters.'
              : 'No documents for this patient — nothing in Kroll and nothing uploaded to PHOX yet.'}
          </Typography>
        </Box>
      )}

      {rows.length > 0 && (
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Title</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Uploaded by</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Size</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key} hover>
                  <TableCell>
                    {/* Pinning needs a PHOX row, not specifically an upload —
                        imported documents stay pinnable. */}
                    {row.phox && (
                      <Tooltip title={row.phox.pinned ? 'Unpin' : 'Pin'}>
                        <IconButton
                          size="small"
                          onClick={() => pinMutation.mutate(row.phox!)}
                          disabled={pinMutation.isPending}
                        >
                          {row.phox.pinned ? (
                            <PinIcon fontSize="small" color="primary" />
                          ) : (
                            <PinOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="body2" noWrap title={row.title}>
                      {row.title}
                    </Typography>
                    {/* WP-10: the prescriptions a document is tied to. */}
                    {row.phox?.rxNumbers && row.phox.rxNumbers.length > 0 && (
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        Rx {row.phox.rxNumbers.join(', ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip describeChild title={row.provenance ?? ''}>
                      <Chip
                        size="small"
                        icon={row.source === 'phox' ? undefined : <KrollIcon />}
                        label={
                          row.source === 'kroll'
                            ? 'Kroll'
                            : row.source === 'kroll_import'
                              ? 'Kroll → PHOX'
                              : 'PHOX'
                        }
                        color={row.source === 'kroll' ? 'default' : 'success'}
                        variant={row.source === 'kroll' ? 'outlined' : 'filled'}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {/* Any row with a PHOX copy is re-categorizable — including
                        an imported one, whose category is a PHOX field. */}
                    {row.phox ? (
                      <Tooltip describeChild title="Click to re-categorize">
                        <Chip
                          size="small"
                          label={row.typeLabel}
                          onClick={(e) =>
                            setRecatAnchor({
                              el: e.currentTarget,
                              doc: row.phox!,
                            })
                          }
                        />
                      </Tooltip>
                    ) : (
                      <Chip size="small" label={row.typeLabel} variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>{row.uploadedBy}</TableCell>
                  <TableCell>
                    <Tooltip
                      // describeChild: the visible date stays the accessible
                      // content; the provenance note is only its description.
                      describeChild
                      title={
                        row.dateIsOriginal
                          ? ''
                          : 'PHOX import date — the original Kroll date is not in the loaded window'
                      }
                    >
                      <Box component="span">
                        {row.date
                          ? format(new Date(row.date), 'MMM d, yyyy h:mm a')
                          : '—'}
                        {!row.dateIsOriginal && ' *'}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{formatBytes(row.sizeBytes)}</TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={
                        oversizedInKroll(row)
                          ? 'Too large to preview via PHOX — open in Kroll'
                          : 'Preview (read-only)'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handlePreview(row)}
                          disabled={
                            previewLoadingKey === row.key || oversizedInKroll(row)
                          }
                        >
                          {previewLoadingKey === row.key ? (
                            <CircularProgress size={16} />
                          ) : (
                            <PreviewIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Download">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(row)}
                          disabled={oversizedInKroll(row)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {row.source === 'kroll' && row.kroll && (
                      <Tooltip
                        title={
                          importingKrollId === row.kroll.krollId
                            ? 'Importing from Kroll…'
                            : row.kroll.tooLargeForPreview
                              ? 'Too large to import via PHOX'
                              : importMutation.isPending
                                ? 'Another import is in progress'
                                : `Save to PHOX as ${PHOX_DOCUMENT_TYPE_LABELS[row.kroll.suggestedCategory]}`
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label={`Save "${row.title}" to PHOX`}
                            // Confirmed, not immediate: this copies a patient
                            // record, and the icon sits one click away from
                            // Preview/Download (GP-1383).
                            onClick={() => setImportCandidate(row.kroll!)}
                            // One import at a time. The panel derives the
                            // spinner from importMutation.variables, which
                            // holds a single row — a second concurrent
                            // import would move the spinner off the row
                            // still loading. The tooltip above says why,
                            // rather than leaving a silently dead button.
                            disabled={
                              importMutation.isPending ||
                              row.kroll.tooLargeForPreview
                            }
                          >
                            {/* GP-1370: pulling a document out of Kroll takes
                                seconds. Spin on the row that was actually
                                clicked so the wait is visibly in progress. */}
                            {importingKrollId === row.kroll.krollId ? (
                              <CircularProgress size={16} />
                            ) : (
                              <ImportIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Re-categorize menu (PHOX rows) */}
      <Menu
        anchorEl={recatAnchor?.el}
        open={Boolean(recatAnchor)}
        onClose={() => setRecatAnchor(null)}
      >
        {RECATEGORIZE_OPTIONS.map((type) => (
          <MenuItem
            key={type}
            selected={recatAnchor?.doc.documentType === type}
            onClick={() => {
              if (recatAnchor) {
                recategorizeMutation.mutate({
                  doc: recatAnchor.doc,
                  documentType: type,
                });
              }
              setRecatAnchor(null);
            }}
          >
            {PHOX_DOCUMENT_TYPE_LABELS[type]}
          </MenuItem>
        ))}
      </Menu>

      {/* Confirm before importing (GP-1383): a copy of a patient record is not
          a side effect of a stray click. */}
      <Dialog
        open={Boolean(importCandidate)}
        onClose={() => setImportCandidate(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save this Kroll document to PHOX?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {importCandidate?.title}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              PHOX will keep its own copy of this document, filed under{' '}
              <strong>
                {importCandidate
                  ? PHOX_DOCUMENT_TYPE_LABELS[importCandidate.suggestedCategory]
                  : ''}
              </strong>
              . The Kroll original is not changed, and the row will still show
              that the document came from Kroll.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You only need a PHOX copy to pin the document, re-categorize it, or
              attach it to PHOX workflows. To just read it, use Preview.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportCandidate(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={importMutation.isPending}
            onClick={() => {
              if (importCandidate) importMutation.mutate(importCandidate);
              setImportCandidate(null);
            }}
          >
            Save to PHOX
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview dialog — read-only; opens straight away with a loading state
          rather than a blank white sheet (GP-1383). */}
      <Dialog
        open={Boolean(preview)}
        onClose={closePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>{preview?.title}</DialogTitle>
        <DialogContent sx={{ height: '75vh', p: 1, position: 'relative' }}>
          {previewBusy && (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1.5}
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'background.paper',
                zIndex: 1,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Loading document…
              </Typography>
            </Stack>
          )}
          {preview?.url && isPreviewImage && (
            <Box
              component="img"
              src={preview.url}
              alt={preview.title}
              onLoad={() => setPreviewRendered(true)}
              onError={() => setPreviewRendered(true)}
              sx={{ maxWidth: '100%', maxHeight: '100%', display: 'block', mx: 'auto' }}
            />
          )}
          {preview?.url && !isPreviewImage && (
            <Box
              component="iframe"
              src={preview.url}
              title={preview.title}
              onLoad={() => setPreviewRendered(true)}
              sx={{ width: '100%', height: '100%', border: 0 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Upload dialog (GP-737) */}
      <PatientDocumentUpload
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        patientId={patientId}
        pharmacyId={activePharmacy?.id || ''}
        onSuccess={() => {
          setUploadOpen(false);
          invalidateBoth();
        }}
      />
    </Paper>
  );
};

export default PatientDocumentsPanel;
